import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import * as calendarApi from '@/api/calendar';
import type {
  CalendarEventsParams,
  CreateEventData,
  UpdateEventData,
  CreateTemplateData,
  UpdateTemplateData,
} from '@/types/calendar';

const CALENDAR_KEY = ['calendar'] as const;
const EVENTS_KEY = [...CALENDAR_KEY, 'events'] as const;
const TEMPLATES_KEY = [...CALENDAR_KEY, 'templates'] as const;

export function useCalendarEvents(params?: CalendarEventsParams) {
  return useQuery({
    queryKey: [...EVENTS_KEY, params],
    queryFn: () => calendarApi.getCalendarEvents(params),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCalendarEvent(id: string | undefined) {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'detail', id],
    queryFn: () => calendarApi.getCalendarEvent(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventData) => calendarApi.createEvent(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEventData }) =>
      calendarApi.updateEvent(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...EVENTS_KEY, 'detail', variables.id],
      });
      void queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarApi.cancelEvent(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
}

export function useGenerateMonth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (month: string) => calendarApi.generateMonth(month),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => calendarApi.getTemplates(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateData) => calendarApi.createTemplate(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateData }) =>
      calendarApi.updateTemplate(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useDeactivateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarApi.deactivateTemplate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useRegenerateFeedToken() {
  return useMutation({
    mutationFn: () => calendarApi.regenerateFeedToken(),
  });
}
